import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { Textarea } from '@/components/ui/textarea.js';
import { skillsGuideUrl } from '@/lib/guide-links.js';
import {
  ApiError,
  createSkill,
  importSkillZip,
  updateSkill,
  uploadSkillResource,
  type CreateSkillBody,
  type Skill,
} from '@/lib/api/skills.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import type { SkillDraft } from '@/lib/api/skill-suggestions.js';

import { SKILL_CHANGED_MESSAGE } from './SkillVersions.js';
import { TagInput } from './TagInput.js';

import {
  BulkImportPreview,
  isBulkRowSettled,
  type BulkImportRow,
} from './BulkImportPreview.js';
import {
  FRONTMATTER_EXTRA_TOO_LONG_NOTE,
  INLINE_MAX,
  FrontmatterError,
  INSTRUCTIONS_MAX,
  isBundleFilename,
  isTriggerValid,
  parseFrontmatter,
  sendableFrontmatterExtra,
  SKILL_IMPORT_ACCEPT,
  slugifyFilename,
  triggerErrorMessage,
  TRIGGER_MAX,
  TRIGGER_RE,
} from './frontmatter.js';

export interface SkillEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present in edit mode; omitted when creating a new skill. */
  skill?: Skill;
  /** Only meaningful in create mode — which tab the dialog opens on. Defaults to `write`. */
  initialTab?: 'write' | 'import';
  /**
   * Only meaningful in create mode: when set, everything authored here (write, zip import, bulk
   * import) is created as a skill owned by that workspace instead of a private one. Null/absent
   * keeps the original private-authoring behavior.
   */
  workspaceId?: string | null;
  /**
   * Create mode only: prefills the write tab from a smart suggestion's draft. The user reviews and
   * saves it themselves — nothing is ever created on their behalf, so the draft is a starting point
   * and not a commitment.
   */
  initialDraft?: SkillDraft | null;
  /** Create mode only: the suggestion this draft came from, reported on save so it can be closed out. */
  suggestionId?: string | null;
  onSaved: (skill: Skill) => void;
}

/** Derives a trigger candidate from a title as the user types it — lowercased, every non-alphanumeric run (spaces included) collapsed to a single hyphen, trimmed, and capped to the 64-char trigger limit with no trailing hyphen. Returns an empty string when nothing alphanumeric survives, so clearing the title clears the auto-filled trigger too. Exported for the unit test. */
export function deriveTriggerFromTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, TRIGGER_MAX)
    .replace(/-+$/, '');
}

/** Turns a kebab trigger into a human title, for a bulk-imported file whose frontmatter has no `name` (e.g. `weekly-report` -> `Weekly Report`). */
function titleCaseFromTrigger(trigger: string): string {
  return trigger
    .split('-')
    .filter(Boolean)
    .map((word) => `${word[0]?.toUpperCase() ?? ''}${word.slice(1)}`)
    .join(' ');
}

/** Hard ceiling on `-N` suffix attempts for a same-batch trigger collision. Bounds `dedupeTrigger` so a batch with many duplicate near-max-length triggers fails that one row instead of hanging the tab. Exported for the exhaustion-path test — not used outside this module otherwise. */
export const DEDUPE_SUFFIX_LIMIT = 99;

/**
 * Appends `-2`, `-3`, … until `trigger` no longer collides with another file already queued in
 * the same bulk batch — the auto-suffix rule for a same-batch collision. Reserves room for the
 * suffix BEFORE truncating to the 64-char trigger cap, so a trigger at or near that cap doesn't
 * have its suffix truncated away and collide with itself forever (a bare
 * `` `${trigger}-${suffix}`.slice(0, 64) `` does exactly that once `trigger` is 63-64 chars).
 * Returns `null` once `DEDUPE_SUFFIX_LIMIT` is exhausted instead of looping further. Exported so
 * the exhaustion path can be unit-tested directly against a pre-populated `used` set, rather than
 * driving 99 file uploads through the dialog.
 */
export function dedupeTrigger(
  trigger: string,
  used: Set<string>,
): string | null {
  if (!used.has(trigger)) return trigger;
  for (let suffix = 2; suffix <= DEDUPE_SUFFIX_LIMIT; suffix += 1) {
    const suffixText = `-${suffix}`;
    const candidate =
      trigger.slice(0, TRIGGER_MAX - suffixText.length).replace(/-+$/, '') +
      suffixText;
    if (!used.has(candidate)) return candidate;
  }
  return null;
}

/** Reads a file as text via `FileReader` — mirrors `DeveloperForm.tsx`'s import pattern rather than `File.prototype.text()`, which some test/runtime environments don't implement. */
function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(reader.error ?? new Error(`could not read ${file.name}`));
    reader.readAsText(file);
  });
}

/**
 * Unified new-skill dialog: create/edit a skill by hand, paste or drop a `.md`/`.txt` export from
 * Claude/ChatGPT (frontmatter prefills the form), drop several at once for a bulk-import preview,
 * or upload a `.zip`/`.skill` bundle (parsed server-side via `importSkillZip`). Edit mode (`skill`
 * set) only ever shows the write form — importing into an existing skill isn't a supported shape.
 */
export function SkillEditorDialog({
  open,
  onOpenChange,
  skill,
  initialTab,
  workspaceId,
  initialDraft,
  suggestionId,
  onSaved,
}: SkillEditorDialogProps): JSX.Element {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<'write' | 'import'>(
    initialTab ?? 'write',
  );
  const [title, setTitle] = useState(skill?.title ?? initialDraft?.title ?? '');
  const [trigger, setTrigger] = useState(
    skill?.trigger ?? initialDraft?.trigger ?? '',
  );
  const [description, setDescription] = useState(
    skill?.description ?? initialDraft?.description ?? '',
  );
  const [instructions, setInstructions] = useState(
    skill?.instructions ?? initialDraft?.instructions ?? '',
  );
  const [tags, setTags] = useState<string[]>(
    skill?.tags ?? initialDraft?.tags ?? [],
  );
  /** Files staged in create mode and uploaded right after the skill row exists (`uploadSkillResource` needs the id). */
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [triggerConflict, setTriggerConflict] = useState(false);
  /** True once the trigger has been set by hand (or by an import prefill), which stops the title from auto-driving it. A prefilled draft counts: its trigger was chosen deliberately and must not be overwritten by the title. */
  const [triggerEdited, setTriggerEdited] = useState(
    initialDraft !== undefined && initialDraft !== null,
  );
  /** The preserved frontmatter lines of an imported file, sent once on create; null for a typed skill. */
  const [frontmatterExtra, setFrontmatterExtra] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [bulkRows, setBulkRows] = useState<BulkImportRow[] | null>(null);
  const [bulkImporting, setBulkImporting] = useState(false);

  const triggerFormatInvalid =
    trigger.length > 0 && !isTriggerValid(trigger, skill?.trigger);
  const triggerErrorText = triggerErrorMessage(
    trigger,
    triggerConflict,
    skill?.trigger,
  );

  /**
   * Title also drives the trigger while creating a skill by hand — until the user edits the trigger
   * themselves (or an import prefilled it) — so a manual create doesn't need the trigger typed
   * twice. Never in edit mode: an existing skill's trigger is only changed deliberately, not as a
   * side effect of retitling.
   */
  function handleTitleChange(value: string): void {
    setTitle(value);
    if (!skill && !triggerEdited) {
      setTrigger(deriveTriggerFromTitle(value));
      setTriggerConflict(false);
    }
  }

  function handleTriggerChange(value: string): void {
    setTrigger(value);
    setTriggerConflict(false);
    setTriggerEdited(true);
  }

  /** Parses pasted or uploaded markdown; on a frontmatter error toasts its message and returns null so the form is left as it was. */
  function readFrontmatterOrNotify(
    text: string,
  ): ReturnType<typeof parseFrontmatter> | null {
    try {
      return parseFrontmatter(text);
    } catch (error) {
      if (!(error instanceof FrontmatterError)) throw error;
      notify.error(error.message);
      return null;
    }
  }

  /**
   * Prefills the write form from parsed markdown (a paste or a single `.md`/`.txt` file) and
   * switches into the Write tab for review before Save. A `name` that doesn't already match
   * `TRIGGER_RE` (or is missing) falls back to a filename-derived slug.
   */
  function applyParsedMarkdown(text: string, fileName?: string): void {
    const parsed = readFrontmatterOrNotify(text);
    if (!parsed) return;
    const nextTrigger =
      parsed.name && TRIGGER_RE.test(parsed.name)
        ? parsed.name
        : slugifyFilename(fileName ?? parsed.name ?? 'pasted-skill');
    setTrigger(nextTrigger);
    setTriggerConflict(false);
    setTriggerEdited(true);
    setTitle(parsed.name ?? titleCaseFromTrigger(nextTrigger));
    setDescription(parsed.description ?? '');
    setInstructions(parsed.body);
    const extra = sendableFrontmatterExtra(parsed.extra);
    if (extra !== parsed.extra) notify.warning(FRONTMATTER_EXTRA_TOO_LONG_NOTE);
    setFrontmatterExtra(extra);
    setActiveTab('write');
  }

  /**
   * Saves the write form — create for a new skill, update for an edit. An edit 409 with a `head`
   * means someone changed the skill meanwhile: the dialog closes and the latest version is shown.
   * Any other 409 means this user already has that trigger; every other failure is already
   * toasted by `assertOk`. In create
   * mode any staged attachments upload right after the row exists; a failed upload is toasted by
   * `assertOk` and skipped (the skill itself is already created), the rest still upload.
   */
  async function handleSave(): Promise<void> {
    if (
      triggerFormatInvalid ||
      !title.trim() ||
      !instructions.trim() ||
      instructions.length > INSTRUCTIONS_MAX
    ) {
      return;
    }
    setSaving(true);
    setTriggerConflict(false);
    try {
      const body: CreateSkillBody = {
        trigger,
        title: title.trim(),
        /** Edit sends '' so clearing the field actually clears the stored description (matching SkillDetail's inline form); create still omits an empty one. */
        description: skill
          ? description.trim()
          : description.trim() || undefined,
        instructions,
        tags,
        /** Create only — a patch never moves a skill between scopes. */
        ...(!skill && workspaceId ? { workspace_id: workspaceId } : {}),
        /**
         * Create only: tells the server this save accepts a smart suggestion, so it can mark the offer
         * accepted and stamp the task signature onto the skill — which is what stops the same task
         * from ever being suggested again.
         */
        ...(!skill && suggestionId ? { suggestion_id: suggestionId } : {}),
        /** Create only: keeps an imported file's license, metadata and the like for export. */
        ...(!skill && frontmatterExtra
          ? { frontmatter_extra: frontmatterExtra }
          : {}),
      };
      const result = skill
        ? await updateSkill(skill.id, body)
        : await createSkill(body);
      if (!skill) {
        for (const file of pendingAttachments) {
          await uploadSkillResource(result.id, file).catch(() => undefined);
        }
      }
      await qc.invalidateQueries({ queryKey: ['skills'] });
      onSaved(result);
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        if (error.head !== null) {
          notify.error(SKILL_CHANGED_MESSAGE);
          await qc.invalidateQueries({ queryKey: ['skills'] });
          onOpenChange(false);
        } else {
          setTriggerConflict(true);
        }
      }
    } finally {
      setSaving(false);
    }
  }

  /** Imports a `.zip`/`.skill` bundle directly — the server parses it (`parseSkillZip`), so a failure is only ever `assertOk`'s toast; there's no trigger field here to map a 409 onto. */
  async function importBundleFile(file: File): Promise<void> {
    setSaving(true);
    try {
      const result = await importSkillZip(file, workspaceId);
      await qc.invalidateQueries({ queryKey: ['skills'] });
      onSaved(result);
      onOpenChange(false);
    } catch {
    } finally {
      setSaving(false);
    }
  }

  /** Parses one non-zip file into a `BulkImportRow`, sanitizing/deduping its trigger against everything already queued in this batch. A `dedupeTrigger` exhaustion (every `-2`..`-99` suffix already taken in this batch) marks the row `invalid` instead of importing an ambiguous trigger. */
  async function parseBulkFile(
    file: File,
    usedTriggers: Set<string>,
  ): Promise<BulkImportRow> {
    const text = await readFileText(file);
    let parsed: ReturnType<typeof parseFrontmatter>;
    try {
      parsed = parseFrontmatter(text);
    } catch (error) {
      if (!(error instanceof FrontmatterError)) throw error;
      return {
        fileName: file.name,
        trigger: slugifyFilename(file.name),
        title: file.name,
        description: '',
        instructions: '',
        frontmatterExtra: null,
        status: 'invalid',
        detail: error.message,
      };
    }
    const candidateTrigger =
      parsed.name && TRIGGER_RE.test(parsed.name)
        ? parsed.name
        : slugifyFilename(parsed.name ?? file.name);
    const rowTrigger = dedupeTrigger(candidateTrigger, usedTriggers);
    const title = parsed.name ?? titleCaseFromTrigger(candidateTrigger);
    const description = parsed.description ?? '';
    if (rowTrigger === null) {
      return {
        fileName: file.name,
        trigger: candidateTrigger,
        title,
        description,
        instructions: parsed.body,
        frontmatterExtra: parsed.extra,
        status: 'invalid',
        detail:
          'Too many duplicate triggers in this batch — rename this skill and import it separately.',
      };
    }
    usedTriggers.add(rowTrigger);
    const tooLong = parsed.body.length > INSTRUCTIONS_MAX;
    const frontmatterExtra = sendableFrontmatterExtra(parsed.extra);
    return {
      fileName: file.name,
      trigger: rowTrigger,
      title: parsed.name ?? titleCaseFromTrigger(rowTrigger),
      description,
      instructions: parsed.body,
      frontmatterExtra,
      status: tooLong ? 'too-long' : 'ready',
      detail: tooLong
        ? `Over ${INSTRUCTIONS_MAX.toLocaleString()} characters — split it, or point the agent at /skill-creator.`
        : frontmatterExtra !== parsed.extra
          ? FRONTMATTER_EXTRA_TOO_LONG_NOTE
          : undefined,
    };
  }

  /** Builds the bulk preview rows for every selected file. A bundle mixed into a multi-file selection is flagged `invalid` — bundles import one at a time. */
  async function buildBulkRows(files: File[]): Promise<BulkImportRow[]> {
    const usedTriggers = new Set<string>();
    const rows: BulkImportRow[] = [];
    for (const file of files) {
      if (isBundleFilename(file.name)) {
        rows.push({
          fileName: file.name,
          trigger: slugifyFilename(file.name),
          title: file.name,
          description: '',
          instructions: '',
          status: 'invalid',
          detail: 'Bundles import one at a time — select just this file.',
        });
        continue;
      }
      rows.push(await parseBulkFile(file, usedTriggers));
    }
    return rows;
  }

  /** Routes a file-input selection: a lone `.zip`/`.skill` bundle imports immediately, a lone `.md`/`.txt` prefills the write form for review, and two or more files open the bulk preview. */
  async function handleFilesSelected(
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;
    const [firstFile] = files;
    if (files.length === 1 && firstFile) {
      if (isBundleFilename(firstFile.name)) {
        await importBundleFile(firstFile);
      } else {
        applyParsedMarkdown(await readFileText(firstFile), firstFile.name);
      }
      return;
    }
    setBulkRows(await buildBulkRows(files));
  }

  /**
   * Creates one bulk row. A 409 (trigger already taken) retries once with a deduped suffix — via
   * `dedupeTrigger`, the same helper the same-batch collision check uses — instead of a bare
   * `` `${trigger}-2`.slice(0, 64) ``, which self-collides forever once `trigger` is 63-64 chars
   * (the suffix gets truncated away, so the retry resubmits the identical trigger and guarantees a
   * second 409). A `null` result (suffix space exhausted against this one trigger) fails the row
   * immediately without a second network call; any other second failure marks the row `failed`.
   */
  async function importBulkRow(row: BulkImportRow): Promise<BulkImportRow> {
    const body: CreateSkillBody = {
      trigger: row.trigger,
      title: row.title,
      description: row.description || undefined,
      instructions: row.instructions,
      ...(workspaceId ? { workspace_id: workspaceId } : {}),
      ...(row.frontmatterExtra
        ? { frontmatter_extra: row.frontmatterExtra }
        : {}),
    };
    try {
      await createSkill(body);
      return { ...row, status: 'done' };
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409) {
        return { ...row, status: 'failed', detail: 'Import failed.' };
      }
      const suffixedTrigger = dedupeTrigger(
        row.trigger,
        new Set([row.trigger]),
      );
      if (suffixedTrigger === null) {
        return {
          ...row,
          status: 'failed',
          detail: 'Trigger already taken, even with a -2 suffix.',
        };
      }
      try {
        await createSkill({ ...body, trigger: suffixedTrigger });
        return { ...row, trigger: suffixedTrigger, status: 'done' };
      } catch {
        return {
          ...row,
          status: 'failed',
          detail: 'Trigger already taken, even with a -2 suffix.',
        };
      }
    }
  }

  /** Sequentially imports every `ready` row, updating each one's live status as it settles. */
  async function runBulkImport(): Promise<void> {
    if (!bulkRows) return;
    setBulkImporting(true);
    const rows = [...bulkRows];
    for (const [index, row] of rows.entries()) {
      if (row.status !== 'ready') continue;
      rows[index] = { ...row, status: 'importing' };
      setBulkRows([...rows]);
      rows[index] = await importBulkRow(row);
      setBulkRows([...rows]);
    }
    setBulkImporting(false);
    await qc.invalidateQueries({ queryKey: ['skills'] });
  }

  /** Cancel/Close on the bulk preview: back to file picking while an import could still run, or close the whole dialog once every row has settled. */
  function handleBulkCancel(): void {
    if (bulkRows && bulkRows.every((row) => isBulkRowSettled(row.status))) {
      onOpenChange(false);
    } else {
      setBulkRows(null);
    }
  }

  const dialogTitle = bulkRows
    ? 'Import skills'
    : skill
      ? 'Edit skill'
      : 'New skill';

  /** The guide section the question mark leads to — the one that explains what is on screen. */
  const guideSection =
    bulkRows || (!skill && activeTab === 'import') ? 'import' : 'write';

  const writeForm = (
    <SkillWriteForm
      title={title}
      trigger={trigger}
      description={description}
      instructions={instructions}
      tags={tags}
      pendingAttachments={skill ? null : pendingAttachments}
      triggerError={triggerErrorText}
      existingTrigger={skill?.trigger}
      isEdit={skill !== undefined}
      saving={saving}
      onTitleChange={handleTitleChange}
      onTriggerChange={handleTriggerChange}
      onDescriptionChange={setDescription}
      onInstructionsChange={setInstructions}
      onTagsChange={setTags}
      onPendingAttachmentsChange={setPendingAttachments}
      onSave={() => void handleSave()}
      onCancel={() => onOpenChange(false)}
    />
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
      helpHref={skillsGuideUrl(guideSection)}
      helpLabel="Open the skills guide"
      className="sm:max-w-[560px]"
    >
      {bulkRows ? (
        <BulkImportPreview
          rows={bulkRows}
          importing={bulkImporting}
          onImport={() => void runBulkImport()}
          onCancel={handleBulkCancel}
        />
      ) : skill ? (
        writeForm
      ) : (
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as 'write' | 'import')}
        >
          <TabsList>
            <TabsTrigger value="write">Write</TabsTrigger>
            <TabsTrigger value="import">Import</TabsTrigger>
          </TabsList>
          <TabsContent value="write">{writeForm}</TabsContent>
          <TabsContent value="import">
            <div className="flex flex-col gap-3 pt-2">
              <Textarea
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                placeholder="Paste a skill's markdown here — frontmatter and all."
                className="min-h-32 max-h-64 overflow-y-auto"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => applyParsedMarkdown(pasteText)}
                disabled={!pasteText.trim()}
              >
                Use pasted text
              </Button>
              <p className="text-xs text-muted-foreground">
                Or drop one or more files — a single .md/.txt prefills the form,
                several open a bulk preview, and a .zip or .skill bundle imports
                directly.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={SKILL_IMPORT_ACCEPT}
                multiple
                data-testid="skill-file-input"
                className="hidden"
                onChange={(event) => void handleFilesSelected(event)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose files…
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </AppDialog>
  );
}

interface SkillWriteFormProps {
  title: string;
  trigger: string;
  description: string;
  instructions: string;
  tags: string[];
  /** Staged create-mode attachments; `null` hides the section entirely (edit mode manages attachments in `SkillDetail`). */
  pendingAttachments: File[] | null;
  triggerError: string | null;
  /** The trigger the skill already has when editing; undefined on create. */
  existingTrigger?: string;
  isEdit: boolean;
  saving: boolean;
  onTitleChange: (value: string) => void;
  onTriggerChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onInstructionsChange: (value: string) => void;
  onTagsChange: (tags: string[]) => void;
  onPendingAttachmentsChange: (files: File[]) => void;
  onSave: () => void;
  onCancel: () => void;
}

/** The manual title/trigger/description/instructions form shared by create and edit — same shape whether typed from scratch or reached after a paste/file import prefilled it. */
function SkillWriteForm({
  title,
  trigger,
  description,
  instructions,
  tags,
  pendingAttachments,
  triggerError,
  existingTrigger,
  isEdit,
  saving,
  onTitleChange,
  onTriggerChange,
  onDescriptionChange,
  onInstructionsChange,
  onTagsChange,
  onPendingAttachmentsChange,
  onSave,
  onCancel,
}: SkillWriteFormProps): JSX.Element {
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const instructionsOverMax = instructions.length > INSTRUCTIONS_MAX;
  const heavy = !instructionsOverMax && instructions.length > INLINE_MAX;
  const canSave =
    title.trim().length > 0 &&
    isTriggerValid(trigger, existingTrigger) &&
    instructions.trim().length > 0 &&
    !instructionsOverMax;

  return (
    <div className="flex flex-col gap-3 pt-2">
      <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Title
        <Input
          value={title}
          onChange={(event) => onTitleChange(event.target.value)}
          placeholder="Weekly report"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Trigger
        <Input
          value={trigger}
          onChange={(event) =>
            onTriggerChange(event.target.value.toLowerCase())
          }
          placeholder="weekly-report"
          aria-invalid={triggerError !== null}
        />
        <span
          className={cn(
            'text-xs',
            triggerError ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {triggerError ?? `Resolved as /${trigger || 'trigger'} in chat.`}
        </span>
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Description
        <Input
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
          placeholder="Use when…"
        />
        <span className="text-xs text-muted-foreground">
          This is what the agent matches on.
        </span>
      </label>
      <div className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Tags
        <TagInput value={tags} onChange={onTagsChange} />
        <span className="text-xs font-normal text-muted-foreground">
          Optional labels for filtering — e.g. a role like ba or qa.
        </span>
      </div>
      <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
        Instructions
        <Textarea
          value={instructions}
          onChange={(event) => onInstructionsChange(event.target.value)}
          className="min-h-32 max-h-64 overflow-y-auto"
        />
        <span
          className={cn(
            'text-xs',
            instructionsOverMax ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {instructions.length.toLocaleString()} /{' '}
          {INSTRUCTIONS_MAX.toLocaleString()}
          {heavy ? ' — heavy skill, delivered via load_skill' : ''}
        </span>
      </label>
      {pendingAttachments !== null && (
        <div className="flex flex-col gap-1.5 text-xs font-medium text-muted-foreground">
          Attachments
          {pendingAttachments.length > 0 && (
            <ul className="flex flex-col gap-1">
              {pendingAttachments.map((file) => (
                <li
                  key={file.name}
                  className="flex items-center justify-between gap-2 text-foreground"
                >
                  <span className="truncate font-normal">{file.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${file.name}`}
                    onClick={() =>
                      onPendingAttachmentsChange(
                        pendingAttachments.filter(
                          (pending) => pending !== file,
                        ),
                      )
                    }
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <input
            ref={attachmentInputRef}
            type="file"
            multiple
            data-testid="skill-create-attachment-input"
            className="hidden"
            onChange={(event) => {
              const picked = Array.from(event.target.files ?? []);
              event.target.value = '';
              if (!picked.length) return;
              const known = new Set(
                pendingAttachments.map((file) => file.name),
              );
              onPendingAttachmentsChange([
                ...pendingAttachments,
                ...picked.filter((file) => !known.has(file.name)),
              ]);
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => attachmentInputRef.current?.click()}
          >
            Add files…
          </Button>
          <span className="text-xs font-normal text-muted-foreground">
            Uploaded with the skill — scripts, templates, reference data (no
            archives).
          </span>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={saving || !canSave}
        >
          {saving ? 'Saving…' : isEdit ? 'Save' : 'Create skill'}
        </Button>
      </div>
    </div>
  );
}
